import { action, observable } from 'mobx';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { StructureController, TerraceDesignToolsController, UgoDesignToolsController } from 'cd-eft/services/eftSetApi';
import { ImageDownload } from 'cd-eft/utils/ImageDownload';
import { OUTPUT_fileFormats } from '../DownloadProject/DownloadProject.config';
import { getErrorFromAPIResponse } from 'cd-eft/utils/getError';
import { appConfig } from 'cd-eft/config/AppConfig';

const libraryStore = stores.AppStore.projectsLibraryStore;

export class PrintConstructionReportsVM {
  @observable props;
  @observable projectId: string;
  @observable sessionId: string;
  @observable designElementId: number = 0;
  @observable designElementTypeId: number = 0;
  // @observable currentStructureList: any = [];
  @observable dataForDesignElement7: any = []; // Waterway
  @observable dataForDesignElement19: any = []; // Lined Waterway
  @observable dataForDesignElement11: any = []; //Pit - Rectagular report data
  @observable dataForDesignElement12: any = []; //Pit - Round report data
  @observable dataForDesignElement13: any = []; //Pit - Diked report data
  @observable dataForDesignElement14: any = []; //Pad report data
  @observable dataForDesignElement15: any = []; //LL report data
  @observable dataForDesignElement20: any = []; // Terrace_GRADIENT
  @observable dataForDesignElement21: any = []; // Terrace_LEVEL
  @observable dataForDesignElement22: any = []; // Terrace_STORAGE
  @observable dataForDesignElement23: any = []; // Terrace_WASCOB
  @observable image: string = '';
  @observable state: string;
  @observable county: string;
  @observable northArrowImage: string = '';
  @observable dataForTerrace: any = [];
  @observable dataForUGOTerrace: any = [];
  @observable dataForUGO: any = [];
  @observable isUGOSelected: boolean = false;
  @observable areIndividualTerracesSelected: boolean = false;
  @observable planMapImage: string = '';
  @observable locationMapImage: string = '';
  constructor(props) {
    this.props = props;
    this.projectId = this.props.project.projectUuid;
    this.sessionId = this.props.project.sessionList[0].sessionUuid;
    this.state = this.props.project.state;
    this.county = this.props.project.county;
  }  
  
  @action.bound async generateUgoConstructionReport(ugoElementIds: number[]) {
    try {
      if (ugoElementIds.length === 0) {
        return [];
      }

      const response = await UgoDesignToolsController.getUgoConstructionReportUsingGET(this.sessionId, ugoElementIds);

      if (response && response.data && response.data.ugoConstructionReportDTO) {
        const newUgoData = response.data.ugoConstructionReportDTO;       
        // Add the alignedElementDesignParamsId to the UGO data for name mapping
        const ugoDataWithId = Array.isArray(newUgoData) 
          ? newUgoData.map((item, index) => ({
              ...item,
              alignedElementDesignParamsId: ugoElementIds[index] || ugoElementIds[0]
            }))
          : [{
              ...(newUgoData as any),
              alignedElementDesignParamsId: ugoElementIds[0]
            }];
        
        this.dataForUGO.push(...ugoDataWithId);
        // Store the map images from the API response for use in shared pages
        if (response.data.planMapImages && response.data.planMapImages.length > 0) {
          this.planMapImage = `data:image/png;base64,${response.data.planMapImages[0]}`;
        }

        if (response.data.locationMapImages && response.data.locationMapImages.length > 0) {
          this.locationMapImage = `data:image/png;base64,${response.data.locationMapImages[0]}`;
        }
      }
      return response;
    } catch (error) {
      MessageCenter.addError((error as Error).message);
      throw error;
    }
  }

  @action.bound async generateConstructionReport() {
    MessageCenter.clearMessages();
    this.resetSelectionState();
    this.dataForDesignElement7 = [];
    this.dataForDesignElement19 = [];
    this.dataForDesignElement11 = [];
    this.dataForDesignElement12 = [];
    this.dataForDesignElement13 = [];
    this.dataForDesignElement14 = [];
    this.dataForDesignElement15 = [];
    this.dataForDesignElement20 = [];
    this.dataForDesignElement21 = [];
    this.dataForDesignElement22 = [];
    this.dataForDesignElement23 = [];
    this.dataForTerrace = [];
    this.dataForUGOTerrace = [];
    this.dataForUGO = [];
    libraryStore.isReportGenerated = false;

    // Define terrace types using hardcodes
    const terraceTypeIds = [
      appConfig.hardCodes.designElementTypeTerraceGradient, // 20
      appConfig.hardCodes.designElementTypeTerraceLevel,    // 21
      appConfig.hardCodes.designElementTypeTerraceStorage,  // 22
      appConfig.hardCodes.designElementTypeTerraceWascob,   // 23
    ];

    if (this.projectId) {
      const northArrowPath = '/mnt/efs/nets/north_arrow.png';
      const northArrow = northArrowPath.split('/').pop();

      try {
        this.northArrowImage = await ImageDownload(northArrow);

        if (libraryStore.designElementsByType) {
          const constructionPayload = {
            elementTypeAndIdListMap: {},
            sessionUuid: this.sessionId,
          };
          let terraceElementIdsToProcess: number[] = [];
          let ugoTerraceElementIdsToProcess: number[] = [];

          // Check if UGO (Terrace System) is selected first
          this.isUGOSelected = false;
          this.areIndividualTerracesSelected = false;

          if (
            libraryStore.designElementsByType['Terrace System'] &&
            libraryStore.designElementsByType['Terrace System'].designElements?.length > 0
          ) {
            const selectedTerraceSystemElements = libraryStore.designElementsByType['Terrace System'].designElements
              .filter((el) => el.isSelected);

            // Check if UGO is selected
            this.isUGOSelected = selectedTerraceSystemElements.some((el) => el.elementType === 'UGO');

            // Check if individual terrace elements are selected
            this.areIndividualTerracesSelected = selectedTerraceSystemElements.some((el) =>
              el.elementType === 'Terrace' && terraceTypeIds.includes(el.terraceTypeId));
          }

          for (const typeId of OUTPUT_fileFormats['Construction'].validTypes) {
            const currentTypeId = typeof typeId === 'string' ? typeId : +typeId;

            if (currentTypeId === 'Terrace System') {
              // Handle Terrace System (both UGO and individual terrace selections)
              if (
                libraryStore.designElementsByType['Terrace System'] &&
                libraryStore.designElementsByType['Terrace System'].designElements?.length > 0
              ) {
                const selectedTerraceSystemElements = libraryStore.designElementsByType['Terrace System'].designElements
                  .filter((el) => el.isSelected);

                if (this.isUGOSelected) {
                  const selectedUgoIds = selectedTerraceSystemElements
                    .filter((el) => el.elementType === 'UGO')
                    .map((el) => el.alignedElementDesignParamsId)
                    .filter((id): id is number => id !== null && id !== undefined);



                  // Generate UGO construction report for each UGO individually
                  if (selectedUgoIds.length > 0) {
                    try {
                      for (let i = 0; i < selectedUgoIds.length; i++) {
                        const ugoId = selectedUgoIds[i];
                        await this.generateUgoConstructionReport([ugoId]);
                      }
                    } catch (error) {
                    }
                  }

                  // Only process UGO terrace if individual terraces are NOT selected
                  // This avoids duplicate API calls when both UGO and individual terraces are selected
                  if (!this.areIndividualTerracesSelected) {
                    const allTerraceIds = selectedTerraceSystemElements
                      .filter((el) => el.elementType === 'Terrace' && terraceTypeIds.includes(el.terraceTypeId))
                      .map((el) => el.alignedElementDesignParamsId)
                      .filter((id): id is number => id !== null && id !== undefined);

                    if (allTerraceIds.length > 0) {
                      ugoTerraceElementIdsToProcess = [allTerraceIds[0]];
                    } else {
                      const anyTerraceIds = selectedTerraceSystemElements
                        .filter((el) => el.elementType === 'Terrace')
                        .map((el) => el.alignedElementDesignParamsId)
                        .filter((id): id is number => id !== null && id !== undefined);

                      if (anyTerraceIds.length > 0) {
                        ugoTerraceElementIdsToProcess = [anyTerraceIds[0]];
                      } else {
                        if (selectedUgoIds.length > 0) {
                          ugoTerraceElementIdsToProcess = [selectedUgoIds[0]];
                        }
                      }
                    }
                  }
                }

                // Process individual terrace selections
                if (this.areIndividualTerracesSelected) {
                  const individualTerraceIds = selectedTerraceSystemElements
                    .filter((el) => el.elementType === 'Terrace' && terraceTypeIds.includes(el.terraceTypeId))
                    .map((el) => el.alignedElementDesignParamsId)
                    .filter((id): id is number => id !== null && id !== undefined);

                  terraceElementIdsToProcess = individualTerraceIds;
                }
              }
            }
            else if (currentTypeId === 'Waterway') {
              if (
                libraryStore.designElementsByType[currentTypeId] &&
                libraryStore.designElementsByType[currentTypeId].designElements?.length > 0
              ) {
                libraryStore.designElementsByType[currentTypeId].designElements
                  .filter((el) => el.isSelected)
                  .forEach((el) => {
                    let wdtTypeId = 0;
                    if (el.waterwayType === 'Grassed') wdtTypeId = appConfig.hardCodes.designElementTypeWaterway; // 7
                    if (el.waterwayType === 'Lined') wdtTypeId = 19;
                    if (wdtTypeId !== 0) {
                      if (!constructionPayload.elementTypeAndIdListMap[wdtTypeId]) {
                        constructionPayload.elementTypeAndIdListMap[wdtTypeId] = [];
                      }
                      // Use alignedElementDesignParamsId for Waterway
                      if (el.alignedElementDesignParamsId !== undefined && el.alignedElementDesignParamsId !== null) {
                        constructionPayload.elementTypeAndIdListMap[wdtTypeId].push(el.alignedElementDesignParamsId);
                      }
                    }
                  });
              }
            }
            else if (
              typeof currentTypeId === 'number' &&
              [11, 12, 13, 14, 15].includes(currentTypeId) &&
              libraryStore.designElementsByType[currentTypeId] &&
              libraryStore.designElementsByType[currentTypeId].designElements?.length > 0
            ) {
              const selectedIds = libraryStore.designElementsByType[currentTypeId].designElements
                .filter((el) => el.isSelected)
                .map((el) => el.designElementId);
              if (selectedIds.length > 0) {
                if (!constructionPayload.elementTypeAndIdListMap[currentTypeId]) {
                  constructionPayload.elementTypeAndIdListMap[currentTypeId] = [];
                }
                constructionPayload.elementTypeAndIdListMap[currentTypeId].push(...selectedIds);
              }
            }
          }

          if (Object.keys(constructionPayload.elementTypeAndIdListMap).length > 0) {
            const collectionResponse = await StructureController.collectConstructionReportsUsingPOST(
              constructionPayload
            );

            if (collectionResponse.data && collectionResponse.data.elementTypeAndReportListMap) {
              for (const [key, value] of Object.entries(collectionResponse.data.elementTypeAndReportListMap)) {
                if (terraceTypeIds.includes(+key)) continue;
                for (const el of value) {
                  const jsonData = JSON.parse(el);
                  const fullPath = jsonData.structureMapUrl;
                  if (fullPath) {
                    const imageName = fullPath.split('/').pop();
                    if (imageName === 'No structure map was found.') {
                      jsonData.structureMapUrl = '';
                    } else {
                      const convertedImageUrl = await ImageDownload(imageName);
                      jsonData.structureMapUrl = convertedImageUrl;
                    }
                  }
                  switch (+key) {
                    case 7:
                      this.dataForDesignElement7.push({ ...jsonData, projectStatus: jsonData.status }); // Waterway
                      break;
                    case 19:
                      this.dataForDesignElement19.push({ ...jsonData, projectStatus: jsonData.status });// Lined Waterway
                      break;
                    case 11:
                      this.dataForDesignElement11.push(jsonData); //Pit - Rectagular report data
                      break;
                    case 12:
                      this.dataForDesignElement12.push(jsonData); //Pit - Round report data
                      break;
                    case 13:
                      this.dataForDesignElement13.push(jsonData); //Pit - Diked report data
                      break;
                    case 14:
                      this.dataForDesignElement14.push(jsonData); //Pad report data
                      break;
                    case 15:
                      const modifiedJsonLL = {
                        ...jsonData,
                        constructionReportPointList: [],
                      };
                      const center = JSON.parse(jsonData.center);
                      if (center) modifiedJsonLL.constructionReportPointList.push(center);

                      const boundaryPoints = JSON.parse(jsonData.boundaryPoints);
                      if (boundaryPoints && boundaryPoints.length > 0) { modifiedJsonLL.constructionReportPointList.push(...boundaryPoints) };

                      const bufferBorder = JSON.parse(jsonData.bufferBorder);
                      if (bufferBorder && bufferBorder.length > 0) { modifiedJsonLL.constructionReportPointList.push(...bufferBorder) };

                      const planePoints = JSON.parse(jsonData.planePoints);
                      if (planePoints && planePoints.length > 0) { modifiedJsonLL.constructionReportPointList.push(...planePoints) };

                      const stakeOutPoints = JSON.parse(jsonData.stakeOutPoints);
                      if (stakeOutPoints && stakeOutPoints.length > 0) { modifiedJsonLL.constructionReportPointList.push(...stakeOutPoints) };

                      this.dataForDesignElement15.push(modifiedJsonLL); //LL report data
                      break;
                    default:
                  }

                }
              }
            }
          }

          // Terrace types (all grouped)
          const uniqueTerraceElementIds = [...new Set(terraceElementIdsToProcess)];

          if (uniqueTerraceElementIds.length > 0) {
            for (const elementId of uniqueTerraceElementIds) {
              const terraceApiPayload = { alignedElementDesignParamsId: elementId };
              const terraceReportResponse = await TerraceDesignToolsController.getTerraceConstructionReportUsingPOST(terraceApiPayload);
              if (terraceReportResponse && terraceReportResponse.data) {
                let terraceJsonData = terraceReportResponse.data;
                const imageFields = ['terracePlanViewImage', 'locationMapImage'];
                for (const field of imageFields) {
                  const imagePath = terraceJsonData[field];
                  if (imagePath) {
                    const imageName = imagePath.split('/').pop();
                    if (imageName && imageName !== 'No structure map was found.') {
                      terraceJsonData[field] = await ImageDownload(imageName);
                    } else {
                      terraceJsonData[field] = '';
                    }
                  } else {
                    terraceJsonData[field] = '';
                  }
                }               
                // Override with UGO map images if available and UGO is selected
                if (this.isUGOSelected) {
                  if (this.planMapImage) {
                    terraceJsonData.terracePlanViewImage = this.planMapImage;
                  }
                  if (this.locationMapImage) {
                    terraceJsonData.locationMapImage = this.locationMapImage;
                  }
                }              
                this.dataForTerrace.push(terraceJsonData);
              }
            }
          }

          // Process UGO terrace types separately
          const uniqueUGOTerraceElementIds = [...new Set(ugoTerraceElementIdsToProcess)];

          if (uniqueUGOTerraceElementIds.length > 0) {
            for (const elementId of uniqueUGOTerraceElementIds) {
              const terraceApiPayload = { alignedElementDesignParamsId: elementId };
              const terraceReportResponse = await TerraceDesignToolsController.getTerraceConstructionReportUsingPOST(terraceApiPayload);
              if (terraceReportResponse && terraceReportResponse.data) {
                let terraceJsonData = terraceReportResponse.data;
                const imageFields = ['terracePlanViewImage', 'locationMapImage'];
                for (const field of imageFields) {
                  const imagePath = terraceJsonData[field];
                  if (imagePath) {
                    const imageName = imagePath.split('/').pop();
                    if (imageName && imageName !== 'No structure map was found.') {
                      terraceJsonData[field] = await ImageDownload(imageName);
                    } else {
                      terraceJsonData[field] = '';
                    }
                  } else {
                    terraceJsonData[field] = '';
                  }
                }
                // Override with UGO map images if available
                if (this.planMapImage) {
                  terraceJsonData.terracePlanViewImage = this.planMapImage;
                }
                if (this.locationMapImage) {
                  terraceJsonData.locationMapImage = this.locationMapImage;
                }
                this.dataForUGOTerrace.push(terraceJsonData);
              }
            }
          }

          libraryStore.isReportGenerated = true;
          this.pushPageNumbers();
        }
      } catch (error) {
        this.props.setPrintButtonClicked(false);
        getErrorFromAPIResponse(error);
        libraryStore.isReportGenerated = false;
      } finally {
        libraryStore.setLoader('');
      }
    }
  }

  @action.bound getSelectedTerraceSystemElements() {
    // Get the selected elements from the library store to provide UGO and Terrace names
    if (libraryStore.designElementsByType['Terrace System'] && 
        libraryStore.designElementsByType['Terrace System'].designElements?.length > 0) {
      return libraryStore.designElementsByType['Terrace System'].designElements
        .filter((el) => el.isSelected);
    }
    return [];
  }

  @action.bound resetSelectionState() {
    this.isUGOSelected = false;
    this.areIndividualTerracesSelected = false;
    this.dataForUGOTerrace = [];
    this.dataForUGO = [];
    this.planMapImage = '';
    this.locationMapImage = '';
  }

  @action.bound pushPageNumbers = () => {
    const elements = document.getElementsByClassName('page-number');
    const total = String(elements.length);
    const totalPages = document.getElementsByClassName('total-pages');
    let i = 1;
    for (const el of elements) {
      el.innerHTML = String(i);
      i++;
    }
    for (const el of totalPages) {
      el.innerHTML = total;
    }
  };
}
